/**
 * Where the AI clients send their requests, and what credential rides along.
 *
 * The app has two deployment shapes and this module is the only thing that knows which one is
 * running:
 *
 *   **Direct.** The original single-user tool. The browser posts to `api.openai.com` with a key the
 *   user pasted, held in `localStorage`. No backend to run, and the key is readable by anything on
 *   the page — an acceptable trade for a local design tool, and stated as such in the README.
 *
 *   **Proxy.** The hosted deployment. The browser posts to this origin's `/api/ai/*`, authenticated
 *   by the session cookie, and the server attaches a key the browser never sees — the operator's, or
 *   the user's own if they supplied one. No credential exists in the page at all.
 *
 * Keeping the choice here rather than in each client means `EnhanceClient`, `SitePlanClient`, and
 * `SwatchClient` build exactly the same request body in both modes; only the URL and the headers
 * differ. The proxy forwards those bodies to OpenAI verbatim, which is what makes that true.
 *
 * Both endpoint maps are read out of `shared/ai/models.ts`, which the proxy reads too. Several
 * operations share one upstream — the wand, the forge, the tagging pre-pass and the five
 * reconstruction stages are all `/v1/responses` calls — but they stay separate operations because
 * the *path* is how the server tells them apart, and telling them apart is what lets usage be
 * priced per feature rather than per endpoint.
 */

import { AI_OPERATION_SPECS, type AiOperation } from '@shared/ai/models';
import { SERVER_REFUSAL_CODES } from '@shared/api';

export type { AiOperation };

/** Everything a `fetch` needs beyond the body itself. */
export interface AiTarget {
  url: string;
  headers: Record<string, string>;
  /** `include` in proxy mode, so the session cookie is sent. Undefined in direct mode. */
  credentials?: RequestCredentials;
}


/**
 * The credential handed around the app when the server holds the real one.
 *
 * The AI features gate on "is there a key" in a dozen places — the enhance panel's run button, the
 * pipeline runner, the texture forge. In proxy mode there is no key in the browser, but the answer
 * to "can we run AI" is still yes. Rather than teach every one of those sites a second concept,
 * `ApiKeyStore` hands out this sentinel and {@link aiTarget} drops it: in proxy mode no
 * `Authorization` header is built at all, so it cannot be sent anywhere even by mistake.
 */
export const MANAGED_CREDENTIAL = '__managed_by_account__';

let proxyMode = false;

/**
 * Switch to server-proxied AI. Called once at boot when a session is found.
 *
 * Module-level state rather than a threaded parameter because it is a property of the deployment,
 * fixed before the first render and identical for every caller — passing it through six layers of
 * UI to reach three clients would be ceremony with no decision in it.
 */
export function useProxiedAi(enabled: boolean): void {
  proxyMode = enabled;
}

/** Whether AI calls currently route through this origin's server. */
export function isProxiedAi(): boolean {
  return proxyMode;
}

/** Resolve the target for one operation. `apiKey` is ignored in proxy mode. */
export function aiTarget(operation: AiOperation, apiKey: string): AiTarget {
  if (proxyMode) {
    return {
      url: AI_OPERATION_SPECS[operation].proxyPath,
      // No Authorization: the session cookie is the credential, and the server attaches the key.
      // `x-glade-app` is the CSRF marker the server requires on every state-changing request; a
      // cross-site form post cannot set a custom header, so its presence proves the call came from
      // this app rather than from a page that merely borrowed the user's cookie.
      headers: { 'x-glade-app': '1' },
      credentials: 'include',
    };
  }
  return {
    url: AI_OPERATION_SPECS[operation].upstream,
    headers: { Authorization: `Bearer ${apiKey}` },
  };
}

/**
 * Whether an error body's `code` is one this app's own server wrote a user-facing sentence for.
 *
 * Out of credits, over the cost cap, suspended, AI switched off — in each case the message already
 * names the state and the way out of it, and the client's job is to show it rather than replace it.
 * All four arrive as a 403 or a 429, where every one of these clients falls through to a sentence
 * about rate limits, API-key permissions, or checking your billing at platform.openai.com — advice
 * that is wrong on a hosted deployment, where the user has no OpenAI account at all.
 *
 * They are also all non-retryable. Retrying is what you do about a rate limit and precisely what
 * does not help here; a reconstruction that retries into the same wall four times turns one clear
 * sentence into four minutes of apparent hanging.
 */
export function isServerRefusal(code: string | null | undefined): boolean {
  return typeof code === 'string' && SERVER_REFUSAL_CODES.includes(code);
}

/** Fetch options for a target, merged with any body-specific headers. */
export function aiRequestInit(target: AiTarget, extraHeaders: Record<string, string> = {}): RequestInit {
  return {
    headers: { ...target.headers, ...extraHeaders },
    ...(target.credentials !== undefined ? { credentials: target.credentials } : {}),
  };
}
